/**
 * The 47 `events_daily` alarm codes, plus the `OP-ABUSE` pseudo-code for
 * `shift_daily.abuse`, annotated for the reliability engines.
 *
 * For each code: the condition-register component it points at (a
 * case-insensitive regex source, matched against the machine's own component
 * names so `rock drill` finds `RH rock drill` on a jumbo and `Rock drill` on a
 * longhole rig), how much the operator can influence it, and the tracked
 * metric it is about.
 *
 * **Proof-of-concept judgement.** The source carries codes and counts only;
 * the component and sensitivity columns are engineering judgement written for
 * this proof of concept and are the first thing a site reliability engineer
 * would be asked to confirm. They are data, not logic, so they can be
 * corrected here without touching an engine.
 *
 * Data Schema layer — seed data.
 *
 * @see src/versions/v1/schema/operations.ts — `AlarmCodeInfo`, `OperatorSensitivity`
 */

import type { MachineClass } from '../../schema/machine.ts';
import type { AlarmCodeInfo } from '../../schema/operations.ts';

const c = (
  label: string,
  component: string | null,
  sensitivity: AlarmCodeInfo['sensitivity'],
  metric: AlarmCodeInfo['metric'] = null,
  sensitiveOn?: readonly MachineClass[],
): AlarmCodeInfo => ({ label, component, sensitivity, metric, ...(sensitiveOn ? { sensitiveOn } : {}) });

/** The pseudo-code `shift_daily.abuse` is attributed under. */
export const ABUSE_CODE = 'OP-ABUSE';

/** Code → annotation. Codes not listed read as `UNKNOWN_CODE`. */
export const ALARM_CODES: Readonly<Record<string, AlarmCodeInfo>> = {
  // Brakes and retarder — condition-driven, made worse by descent technique.
  'W-BRK-TEMP': c('Brake temperature high', 'retarder|brake', 'partial', 'rear_brake_temp_c', ['truck']),
  'W-RET-DUTY': c('Retarder duty high', 'retarder', 'partial', 'rear_brake_temp_c', ['truck']),
  'E-BRK-TEMP-HIGH': c('Brake temperature critical', 'retarder|brake', 'partial', 'rear_brake_temp_c', ['truck']),
  'W-BRK-WEAR': c('Brake wear indicator', 'retarder|brake', 'none'),
  // Hydraulics — technique on a loader (boom-overload holds), a cooler problem on a drill.
  'W-HYD-TEMP': c('Hydraulic oil temperature high', 'hydraulic', 'partial', 'hyd_oil_temp_c', ['lhd']),
  'W-HYD-FILTER': c('Hydraulic filter restriction', 'hydraulic', 'none'),
  'W-HYD-LEVEL-LOW': c('Hydraulic oil level low', 'hydraulic', 'none'),
  'E-HYD-PRESS-LOW': c('Hydraulic pressure low', 'hydraulic', 'none'),
  'E-HYD-PUMP-DRIVE': c('Hydraulic pump drive fault', 'hydraulic pump', 'none'),
  // Rock drills.
  'W-PERC-PRESS': c('Percussion pressure out of band', 'rock drill', 'none', 'percussion_pressure_bar'),
  'W-ACC-PRECHARGE': c('Accumulator pre-charge low', 'rock drill', 'none'),
  'W-FLUSH-LEAK': c('Flushing water leak', 'water|flushing|rock drill', 'none'),
  // Surface drills.
  'W-PEN-RATE': c('Penetration rate low', 'dth|rock drill', 'none', 'penetration_rate_m_h'),
  'W-PEN-RATE-LOW': c('Penetration rate very low', 'dth|rock drill', 'none', 'penetration_rate_m_h'),
  'W-AIR-PRESS': c('Air pressure out of band', 'compressor|dth', 'none'),
  'W-CMP-TEMP': c('Compressor temperature high', 'compressor', 'none'),
  'W-ROT-TORQUE': c('Rotation torque high', 'rotary|rock drill', 'partial'),
  'W-ROT-TORQUE-OSC': c('Rotation torque oscillation', 'rotary', 'none'),
  'W-ROT-CASE-DRAIN': c('Rotary motor case-drain flow high', 'rotary', 'none'),
  'E-ROT-OVERSPEED': c('Rotation overspeed', 'rotary', 'partial'),
  'W-BREAKOUT-TORQUE': c('Breakout torque high', 'rotary|rod|pipe', 'high'),
  'E-PIPE-PARTED': c('Drill string parted', 'dth|pipe', 'partial'),
  'E-ROD-DROP': c('Rod dropped', 'rod|carousel|pipe', 'high'),
  'W-DUST-HIGH': c('Dust high — suppression off', 'dust', 'high'),
  'W-DC-VACUUM': c('Dust collector vacuum low', 'dust', 'none'),
  'W-TRK-TENSION': c('Track tension out of band', 'track', 'none'),
  // Engine and driveline.
  'W-ENG-DERATE': c('Engine derate', '^engine$|carrier engine', 'none'),
  'W-BOOST-LOW': c('Boost pressure low', '^engine$|carrier engine', 'none'),
  'W-EXH-TEMP': c('Exhaust temperature high', '^engine$|carrier engine', 'partial', null, ['truck', 'lhd']),
  'E-ENG-BAY-TEMP': c('Engine bay temperature critical', '^engine$|fire', 'none'),
  'W-TRN-SLIP': c('Transmission slip', 'transmission|torque converter', 'partial'),
  'W-TRN-OIL-TEMP': c('Transmission oil temperature high', 'transmission', 'partial'),
  'W-TYRE-PRESS': c('Tyre pressure out of band', 'tyre', 'none'),
  // Electrical.
  'W-ELEC-EARTH': c('Earth leakage', 'electric motor|traction|battery pack', 'none'),
  'E-ELEC-EARTH': c('Earth fault', 'electric motor|traction|battery pack', 'none'),
  'W-CABLE-TENSION': c('Trailing cable tension', 'cable reel', 'none'),
  // Battery-electric.
  'E-BAT-ISO': c('Battery isolation fault', 'battery pack', 'none'),
  'W-BAT-DERATE': c('Battery derate', 'battery pack', 'none'),
  'W-BAT-TEMP-SPREAD': c('Battery cell temperature spread', 'battery thermal|battery pack', 'none'),
  'W-SOC-LOW': c('State of charge low — run flat', 'battery pack', 'high'),
  'W-CHG-HANDSHAKE': c('Charge handshake failed', 'charging', 'partial'),
  'E-CHG-HANDSHAKE': c('Charge handshake fault', 'charging', 'partial'),
  'W-CHG-WAIT': c('Waiting for charger', 'charging', 'none'),
  // Autonomy, remote and safety.
  'W-AUTO-ABORT': c('Autonomous cycle aborted', 'autonomy', 'none'),
  'W-VIDEO-DROP': c('Tele-remote video dropout', 'remote control', 'none'),
  'W-FIRE-SUPP-LOW': c('Fire suppression pressure low', 'fire', 'none'),
  'E-FIRE-SUPP-DISCHARGE': c('Fire suppression discharged', 'fire', 'none'),
};

/** Annotation for a code the table does not list. */
export const UNKNOWN_CODE: AlarmCodeInfo = c('Unclassified alarm', null, 'none');

/**
 * `OP-ABUSE` points at the component the class's abuse events wear out:
 * bucket ramming and boom overload on a loader, brake-throttle overlap and
 * wrong-gear selection on a truck, high-feed collaring on a drill.
 */
export const ABUSE_COMPONENT: Readonly<Record<MachineClass, string>> = {
  lhd: 'bucket & get',
  truck: 'transmission',
  jumbo: 'rock drill',
  longhole: 'rock drill',
  sdrill: 'rock drill|dth|rotary',
};

/**
 * Annotation for `code` on a machine of `cls`, with `sensitivity` already
 * narrowed to `'none'` where the code's `sensitiveOn` excludes the class.
 */
export function alarmInfo(code: string, cls: MachineClass): AlarmCodeInfo {
  if (code === ABUSE_CODE) return c('Operator abuse event', ABUSE_COMPONENT[cls], 'high');
  const info = ALARM_CODES[code] ?? UNKNOWN_CODE;
  return info.sensitiveOn && !info.sensitiveOn.includes(cls) ? { ...info, sensitivity: 'none' } : info;
}
